import { FC, HTMLAttributes, MouseEvent as ReactMouseEvent, useCallback, useMemo, useState } from "react";

import Stack from "@mui/material/Stack";
import { useController, useFormContext } from "react-hook-form";

import { useRateFieldName } from "../../hooks/useRateFieldName";

import { formatRatePpmAsPercent } from "./helpers";
import { TaxFormModal } from "./TaxFormModal";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import {
  AutocompleteOption,
  AutocompleteV2Option,
  DashboardAutocompleteV2,
} from "@/dashboard-design-system/DashboardAutocompleteV2";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { TicketingTax, useTicketingTaxes } from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useTicketingTaxes";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import { Add, Edit } from "@/icons/outlined";

type TaxOption = AutocompleteV2Option<string>;

export const TaxesField: FC = () => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { control, getValues, setValue } = useFormContext<StepTicketsFormData>();
  const { taxes, getTaxById } = useTicketingTaxes();

  const fieldName = useRateFieldName("taxIds");

  const {
    field: { value: selectedIds, onChange },
  } = useController({ control, name: fieldName });

  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [editingTaxId, setEditingTaxId] = useState<string | null>(null);
  const editingTax = editingTaxId ? (getTaxById(editingTaxId) ?? null) : null;

  const options = useMemo<TaxOption[]>(
    () =>
      taxes.map((tax) => ({
        label: `${tax.name} - ${formatRatePpmAsPercent(tax.ratePpm, locale)}%`,
        value: tax.id,
      })),
    [taxes, locale]
  );

  const selectedOptions = useMemo<TaxOption[]>(() => {
    const ids = (selectedIds as string[] | undefined) ?? [];
    return ids
      .map((id) => options.find((option) => option.value === id))
      .filter((option): option is TaxOption => Boolean(option));
  }, [options, selectedIds]);

  const handleChange = useCallback(
    (value: TaxOption[] | undefined) => {
      onChange((value ?? []).map((option) => option.value));
    },
    [onChange]
  );

  const handleCreated = useCallback(
    (tax: TicketingTax) => {
      const currentIds = (selectedIds as string[] | undefined) ?? [];
      if (!currentIds.includes(tax.id)) {
        onChange([...currentIds, tax.id]);
      }
    },
    [onChange, selectedIds]
  );

  const handleDeleted = useCallback(
    (deletedId: string) => {
      const currentRates = getValues("rates") ?? [];
      currentRates.forEach((rate, index) => {
        const ids = rate.taxIds ?? [];
        if (ids.includes(deletedId)) {
          setValue(
            `rates.${index}.taxIds`,
            ids.filter((id) => id !== deletedId),
            { shouldDirty: true }
          );
        }
      });
    },
    [getValues, setValue]
  );

  const renderOption = useCallback(
    (liProps: HTMLAttributes<HTMLLIElement>, option: TaxOption, { selected }: { selected: boolean }) => {
      const tax = getTaxById(option.value);
      if (!tax) {
        return null;
      }
      const editButton = (
        <DashboardIconButton
          size="small"
          vibe="neutral"
          disabled={tax.isUsedInPurchase}
          aria-label={t("dashboard", "ticketing.taxes.field.edit")}
          onMouseDown={preventRowSelection}
          onClick={(event) => {
            preventRowSelection(event);
            setEditingTaxId(tax.id);
          }}
        >
          <Edit />
        </DashboardIconButton>
      );
      return (
        <AutocompleteOption
          {...liProps}
          key={option.value}
          label={tax.name}
          secondaryLabel={`${formatRatePpmAsPercent(tax.ratePpm, locale)}%`}
          checkbox
          checked={selected}
          selected={selected}
          size="small"
          action={
            tax.isUsedInPurchase ? (
              <DashboardTooltip title={t("dashboard", "taxes.manage.lockedTooltip")}>
                <span style={{ display: "inline-flex" }}>{editButton}</span>
              </DashboardTooltip>
            ) : (
              editButton
            )
          }
        />
      );
    },
    [getTaxById, locale, t]
  );

  return (
    <>
      <Stack sx={{ flexGrow: 1, minWidth: 0, width: { sm: 300 }, maxWidth: 300 }}>
        <DashboardAutocompleteV2<TaxOption, true>
          multiple
          name={fieldName}
          size="small"
          optionSize="small"
          placeholder={t("dashboard", "ticketing.taxes.field.placeholder")}
          searchPlaceholder={t("dashboard", "ticketing.taxes.field.searchPlaceholder")}
          options={options}
          value={selectedOptions}
          onChange={handleChange}
          noOptionsText={<></>}
          openOnFocus
          disableTagOverflow
          chipProps={{ shape: "rounded", variant: "filled" }}
          slotProps={{ paper: { sx: { "& .MuiAutocomplete-noOptions": { display: "none" } } } }}
          sx={{ "& .MuiInputBase-input": { caretColor: "auto !important" } }}
          renderOption={renderOption}
          footer={{
            type: "alternate",
            actions: [
              {
                label: t("dashboard", "ticketing.taxes.field.newTax"),
                icon: <Add />,
                onClick: () => setIsNewModalOpen(true),
              },
            ],
          }}
        />
      </Stack>
      <TaxFormModal
        open={isNewModalOpen}
        tax={null}
        onClose={() => setIsNewModalOpen(false)}
        onCreated={handleCreated}
      />
      <TaxFormModal
        open={editingTax !== null}
        tax={editingTax}
        onClose={() => setEditingTaxId(null)}
        onDeleted={handleDeleted}
      />
    </>
  );
};

const preventRowSelection = (event: ReactMouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};
